// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { formatNumber } from '@/utilities/number';
import {
  Chip,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { ReactNode } from 'react';

/**
 * Represents a single option in the filter toggle group.
 */
export interface FilterOption<Value extends string> {
  /**
   * The value associated with this option.
   */
  value: Value;
  /**
   * The label to display for this option.
   */
  label: string;
  /**
   * Optional icon to display before the label.
   */
  icon?: ReactNode;
  /**
   * Optional tooltip to show for the option.
   */
  tooltip?: string;
  /**
   * Optional count displayed after the label. Undefined renders no chip;
   * zero renders a zero chip.
   */
  count?: number;
}

/**
 * Props for the FilterToggleGroup component.
 */
export interface FilterToggleGroupProps<Value extends string> {
  /**
   * The currently selected value.
   */
  value: Value;
  /**
   * The options to display in the toggle group.
   */
  options: FilterOption<Value>[];
  /**
   * Callback when the selection changes.
   */
  onChange: (value: Value) => void;
  /**
   * Aria label for accessibility.
   */
  ariaLabel: string;
  /**
   * Size of the toggle buttons.
   */
  size?: 'small' | 'medium' | 'large';
  /**
   * Whether only one value can be selected at a time.
   */
  exclusive?: boolean;
  /**
   * When true the group spans the full width of its container and each
   * button takes an equal share. Useful in dialog headers / drawer
   * banners where the toggle is the row's sole element.
   */
  fullWidth?: boolean;
}

/**
 * A reusable toggle button group component with consistent styling.
 * Uses Typography with button variant and no text transform, wrapped in a Stack.
 */
export function FilterToggleGroup<Value extends string>(
  props: FilterToggleGroupProps<Value>
) {
  const {
    value,
    options,
    onChange,
    ariaLabel,
    size = 'small',
    exclusive = true,
    fullWidth = false,
  } = props;

  return (
    <ToggleButtonGroup
      value={value}
      exclusive={exclusive}
      size={size}
      onChange={(_, newValue) => {
        if (newValue !== null) {
          onChange(newValue);
        }
      }}
      aria-label={ariaLabel}
      sx={
        fullWidth
          ? { width: '100%', '& .MuiToggleButton-root': { flex: 1 } }
          : undefined
      }
    >
      {options.map((option) => (
        <Tooltip key={option.value} title={option.tooltip ?? ''} arrow>
          <ToggleButton value={option.value}>
            <Stack direction="row" alignItems="center" spacing={1} paddingX={1}>
              {option.icon}
              <Typography variant="button" textTransform="none">
                {option.label}
              </Typography>
              {option.count !== undefined && (
                <Chip size="small" label={formatNumber(option.count)} />
              )}
            </Stack>
          </ToggleButton>
        </Tooltip>
      ))}
    </ToggleButtonGroup>
  );
}
